import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { tenantUrl } from '../../lib/site';
import { Logo } from '../../components/Logo';

const LAST_KEY = 'goyone.lastCompany';

/**
 * «ادخل إلى شركتك» — صفحة الدخول على الموقع العامّ.
 *
 * لا دخولَ على goyone.com نفسه: كل شركة تدخل من نطاقها (sunmed.goyone.com).
 * فهذه الصفحة تسأل عن معرّف الشركة وتحوّل إليه، بدل نموذج دخولٍ لا يعرف
 * لأيّ شركة يُرسل.
 */
export function FindCompany() {
  const [slug, setSlug] = useState(() => {
    try {
      return localStorage.getItem(LAST_KEY) ?? '';
    } catch {
      return '';
    }
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const root = window.location.hostname.replace(/^www\./, '');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const clean = slug.trim().toLowerCase();
    if (!clean) return;
    setError(null);
    setBusy(true);
    try {
      // checkSlug يردّ «متاح» للمعرّف غير المأخوذ — أي لا شركة به.
      const r = await api<{ available: boolean }>(`/public/slug?slug=${encodeURIComponent(clean)}`);
      if (r.available) {
        setError('لا توجد شركة بهذا المعرّف. تأكّد منه أو سجّل شركتك.');
        setBusy(false);
        return;
      }
      try {
        localStorage.setItem(LAST_KEY, clean);
      } catch {
        /* التخزين قد يُمنع — لا يعطّل التحويل. */
      }
      // رابطٌ داخليّ فُتح على الموقع العامّ يُكمَل على نطاق الشركة بعد الدخول.
      const next = new URLSearchParams(window.location.search).get('next');
      window.location.href = tenantUrl(clean, next ? `/login?next=${encodeURIComponent(next)}` : '/login');
    } catch {
      setError('تعذّر الاتصال بالخادم. تحقق من الشبكة.');
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <form className="login-card" onSubmit={onSubmit} noValidate>
        <div className="login-brand">
          <Logo size={34} />
          <span>GoyOne</span>
        </div>
        <p className="login-sub">ادخل إلى حساب شركتك</p>

        {error && (
          <div className="alert alert-danger mb-4" role="alert">
            {error}
          </div>
        )}

        <div className="login-form">
          <div className="field">
            <label className="label req" htmlFor="company">
              معرّف الشركة
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} dir="ltr">
              <input
                id="company"
                className="input"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="your-company"
                autoComplete="organization"
                autoFocus
              />
              <span className="mute small">.{root}</span>
            </div>
            <span className="tiny mute">
              المعرّف الذي اخترتَه عند تسجيل الشركة — ورابطُ دخولكم الدائم هو
              <span className="num"> معرّف.{root}</span>
            </span>
          </div>

          <button className="btn btn-primary" type="submit" disabled={busy || !slug.trim()}>
            {busy ? 'جارٍ البحث' : 'متابعة'}
          </button>
          <p className="tiny mute" style={{ textAlign: 'center', margin: 0 }}>
            ليس لشركتك حساب؟ <Link to="/signup">سجّل الآن</Link>
          </p>
        </div>
      </form>
    </div>
  );
}
